type ViewCategoryEvent = {
  event: 'view_category';
  category_name: string;
};

type ViewCategoryParams = {
  categoryName: string;
};

type UpdateProfileEvent = {
  event: 'update_profile';
};

type UpdatePasswordEvent = {
  event: 'update_password';
};

type ClickTwygsBotEvent = {
  event: 'click_twygs_bot';
};

type ClickFactCheckerEvent = {
  event: 'click_fact_checker';
};

type FeedbackEvent = {
  event: 'feedback';
  feedback_type: 'bug' | 'feedback';
};

type ArticleViewEvent = {
  event: 'article_view';
  article_type: string;
  article_id: string;
};

type ArticleShareEvent = {
  event: 'article_share';
  article_type: string;
  article_id: string;
};

type ArticleBookmarkEvent = {
  event: 'article_bookmark';
  article_type: string;
  article_id: string;
};

type ArticleCommentEvent = {
  event: 'article_comment';
  article_type: string;
  article_id: string;
};

type ArticleReportEvent = {
  event: 'article_report';
  article_type: string;
  article_id: string;
};

type ArticleIgnoreEvent = {
  event: 'article_ignore';
  article_type: string;
  article_id: string;
};

type ArticleReactEvent = {
  event: 'article_react';
  article_type: string;
  article_id: string;
  reaction_type: string;
};

type ArticleAwardEvent = {
  event: 'article_award';
  article_type: string;
  article_id: string;
};

type ArticleInvestEvent = {
  event: 'article_invest';
  article_type: string;
  article_id: string;
  seed_count: number;
};

type ParagraphCommentEvent = {
  event: 'paragraph_comment';
  article_type: string;
  article_id: string;
};

type ParagraphReactEvent = {
  event: 'paragraph_react';
  article_type: string;
  article_id: string;
};

type CommentAwardEvent = {
  event: 'comment_award';
  article_type: string;
  article_id: string;
};

type ArticleDraftEvent = {
  event: 'article_draft';
  article_type: string;
  article_id: string;
};

type ArticlePublishEvent = {
  event: 'article_publish';
  article_type: string;
  article_id: string;
};

type ContentRequestEvent = {
  event: 'content_request';
  content_type: string;
  username_request: string;
};

type ContentSubmissionEvent = {
  event: 'content_submission';
  content_type: string;
  username_submission: string;
};

type ContentAcceptEvent = {
  event: 'content_accept';
  content_type: string;
  username_request: string;
};

type ContentRejectEvent = {
  event: 'content_reject';
  content_type: string;
  username_request: string;
};

type WriterFollowEvent = {
  event: 'writer_follow';
  writer_username: string;
};

type WriterReportEvent = {
  event: 'writer_report';
  writer_username: string;
};

type WriterIgnoreEvent = {
  event: 'writer_ignore';
  writer_username: string;
};

type WriterShareEvent = {
  event: 'writer_share';
  writer_username: string;
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function pushToDataLayer(payload: Record<string, unknown>): void {
  if (!isBrowser()) return;

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

export const DataLayer = {
  trackViewCategory(params: ViewCategoryParams): void {
    const { categoryName } = params;
    const event: ViewCategoryEvent = {
      event: 'view_category',
      category_name: categoryName
    };
    pushToDataLayer(event);
  },

  trackUpdateProfile(): void {
    const event: UpdateProfileEvent = {
      event: 'update_profile'
    };
    pushToDataLayer(event);
  },

  trackUpdatePassword(): void {
    const event: UpdatePasswordEvent = {
      event: 'update_password'
    };
    pushToDataLayer(event);
  },

  trackClickTwygsBot(): void {
    const event: ClickTwygsBotEvent = {
      event: 'click_twygs_bot'
    };
    pushToDataLayer(event);
  },

  trackClickFactChecker(): void {
    const event: ClickFactCheckerEvent = {
      event: 'click_fact_checker'
    };
    pushToDataLayer(event);
  },

  trackFeedback(feedbackType: 'bug' | 'feedback'): void {
    const event: FeedbackEvent = {
      event: 'feedback',
      feedback_type: feedbackType
    };
    pushToDataLayer(event);
  },

  trackArticleView(articleType: string, articleId: string): void {
    const event: ArticleViewEvent = {
      event: 'article_view',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleShare(articleType: string, articleId: string): void {
    const event: ArticleShareEvent = {
      event: 'article_share',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleBookmark(articleType: string, articleId: string): void {
    const event: ArticleBookmarkEvent = {
      event: 'article_bookmark',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleComment(articleType: string, articleId: string): void {
    const event: ArticleCommentEvent = {
      event: 'article_comment',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleReport(articleType: string, articleId: string): void {
    const event: ArticleReportEvent = {
      event: 'article_report',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleIgnore(articleType: string, articleId: string): void {
    const event: ArticleIgnoreEvent = {
      event: 'article_ignore',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleReact(articleType: string, articleId: string, reactionType: string): void {
    const event: ArticleReactEvent = {
      event: 'article_react',
      article_type: articleType,
      article_id: articleId,
      reaction_type: reactionType
    };
    pushToDataLayer(event);
  },

  trackArticleAward(articleType: string, articleId: string): void {
    const event: ArticleAwardEvent = {
      event: 'article_award',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleInvest(articleType: string, articleId: string, seedCount: number): void {
    const event: ArticleInvestEvent = {
      event: 'article_invest',
      article_type: articleType,
      article_id: articleId,
      seed_count: seedCount
    };
    pushToDataLayer(event);
  },

  trackParagraphComment(articleType: string, articleId: string): void {
    const event: ParagraphCommentEvent = {
      event: 'paragraph_comment',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackParagraphReact(articleType: string, articleId: string): void {
    const event: ParagraphReactEvent = {
      event: 'paragraph_react',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackCommentAward(articleType: string, articleId: string): void {
    const event: CommentAwardEvent = {
      event: 'comment_award',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticleDraft(articleType: string, articleId: string): void {
    const event: ArticleDraftEvent = {
      event: 'article_draft',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackArticlePublish(articleType: string, articleId: string): void {
    const event: ArticlePublishEvent = {
      event: 'article_publish',
      article_type: articleType,
      article_id: articleId
    };
    pushToDataLayer(event);
  },

  trackContentRequest(contentType: string, usernameRequest: string): void {
    const event: ContentRequestEvent = {
      event: 'content_request',
      content_type: contentType,
      username_request: usernameRequest
    };
    pushToDataLayer(event);
  },

  trackContentSubmission(contentType: string, usernameSubmission: string): void {
    const event: ContentSubmissionEvent = {
      event: 'content_submission',
      content_type: contentType,
      username_submission: usernameSubmission
    };
    pushToDataLayer(event);
  },

  trackContentAccept(contentType: string, usernameRequest: string): void {
    const event: ContentAcceptEvent = {
      event: 'content_accept',
      content_type: contentType,
      username_request: usernameRequest
    };
    pushToDataLayer(event);
  },

  trackContentReject(contentType: string, usernameRequest: string): void {
    const event: ContentRejectEvent = {
      event: 'content_reject',
      content_type: contentType,
      username_request: usernameRequest
    };
    pushToDataLayer(event);
  },

  trackWriterFollow(writerUsername: string): void {
    const event: WriterFollowEvent = {
      event: 'writer_follow',
      writer_username: writerUsername
    };
    pushToDataLayer(event);
  },

  trackWriterReport(writerUsername: string): void {
    const event: WriterReportEvent = {
      event: 'writer_report',
      writer_username: writerUsername
    };
    pushToDataLayer(event);
  },

  trackWriterIgnore(writerUsername: string): void {
    const event: WriterIgnoreEvent = {
      event: 'writer_ignore',
      writer_username: writerUsername
    };
    pushToDataLayer(event);
  },

  trackWriterShare(writerUsername: string): void {
    const event: WriterShareEvent = {
      event: 'writer_share',
      writer_username: writerUsername
    };
    pushToDataLayer(event);
  }
};

export default DataLayer;
